import React from 'react';
import { ChartCard } from '@/components/visuals/ChartCard';
import type { ChartDefinition } from '@/pages/analytics/overview/registry';
import { cn } from '@/lib/utils';

const MAX_PINNED_ON_OVERVIEW = 6;

export interface PinnedChartsGridProps {
  charts: ChartDefinition[];
  pinnedIds: string[];
  renderChart: (chartId: string, height: number | string) => React.ReactNode;
  onSelectChart: (chartId: string) => void;
  onTogglePin: (chartId: string) => void;
  onInfoClick?: (chart: ChartDefinition) => void;
  /** Navigate to Library with pinned filter */
  onViewAllPinned?: () => void;
}

/**
 * Pinned charts section for the Overview cockpit.
 * Shows a maximum of 6 pinned chart cards. If more are pinned, a "View all pinned" link appears.
 */
export const PinnedChartsGrid: React.FC<PinnedChartsGridProps> = ({
  charts,
  pinnedIds,
  renderChart,
  onSelectChart,
  onTogglePin,
  onInfoClick,
  onViewAllPinned,
}) => {
  const pinnedSet = new Set(pinnedIds);
  const pinnedCharts = charts.filter((c) => pinnedSet.has(c.id));
  const visiblePinned = pinnedCharts.slice(0, MAX_PINNED_ON_OVERVIEW);
  const hasMore = pinnedCharts.length > MAX_PINNED_ON_OVERVIEW;

  if (pinnedCharts.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No pinned charts. Pin charts from the{' '}
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={onViewAllPinned}
        >
          Library
        </button>{' '}
        to see them here.
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visiblePinned.map((chart) => {
          const isPinned = pinnedSet.has(chart.id);
          return (
            <ChartCard
              key={chart.id}
              title={chart.title}
              subtitle={chart.description}
              variant="compact"
              compactChartHeight={chart.compactHeight}
              onClick={() => onSelectChart(chart.id)}
              onInfoClick={onInfoClick ? () => onInfoClick(chart) : undefined}
              actions={
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onTogglePin(chart.id);
                  }}
                  className={cn(
                    'shrink-0 px-2 py-0.5 text-xs rounded border transition-colors',
                    isPinned
                      ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
                      : 'text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                  )}
                  title={isPinned ? 'Unpin from Overview' : 'Pin to Overview'}
                >
                  {isPinned ? 'Unpin' : 'Pin'}
                </button>
              }
            >
              {renderChart(chart.id, '100%')}
            </ChartCard>
          );
        })}
      </div>
      {hasMore && onViewAllPinned && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onViewAllPinned}
            className="text-xs text-primary hover:underline"
          >
            View all {pinnedCharts.length} pinned charts in Library
          </button>
        </div>
      )}
    </div>
  );
};
